import { useEffect, useMemo } from "react";

import useApiRequestPost from "../../components/apiHooks/useApiRequestPost";
import { useConfigActions } from "../../contexts/ConfigContext/ConfigContext";
import { setDatadogGlobalContext, setDatadogUserProperty } from "../../services/datadog";
import { ICheckQrSessionResponse } from "./types";

const useCheckQrSession = (bbUuid: string, qrsId: string) => {
	const actions = useConfigActions();

	const payload = useMemo(() => {
		const host = process.env.CLOUD_RUN_GATEWAY_HOST ?? "";
		const endpoint = process.env.API_QR_SESSION_CHECK ?? "";
		// Empty string is a falsy SWR key — useApiRequestPost skips the request when either param is missing.
		const url = bbUuid && qrsId ? `${host}${endpoint}` : "";

		return {
			url,
			triggerOnMount: true,
			body: {
				bb_uuid: bbUuid,
				qrs_id: qrsId,
			},
			swrConfig: {
				revalidateOnFocus: false,
			},
		};
	}, [bbUuid, qrsId]);

	const { data: response, isLoading, error } = useApiRequestPost<ICheckQrSessionResponse>(payload);

	// `actions` is stable across renders — createContextReducer wraps it in useMemo([dispatch, caseReducers]),
	// both of which are referentially stable, so this dependency does not cause re-render loops.
	useEffect(() => {
		if (!response?.data?.attributes) return;

		const { id, attributes } = response.data;

		actions.SET_QR_SESSION({
			qrs_id: id,
			pax: attributes.pax,
			table: attributes.table,
			status: attributes.status,
			bb_uuid: attributes.bb_uuid,
			order_until: attributes.order_until,
		});

		// Attach QR-session attributes as Datadog RUM global context so every
		// subsequent view / action / error / resource event is automatically
		// decorated. Also stamp qrs_id on the user object so user.qrs_id is
		// filterable from the RUM Explorer User panel. Re-firing on session
		// change overwrites all values.
		setDatadogGlobalContext("qrs_id", id);
		setDatadogGlobalContext("bb_uuid", attributes.bb_uuid);
		setDatadogGlobalContext("table", attributes.table);
		setDatadogGlobalContext("status", attributes.status);
		setDatadogGlobalContext("pax", attributes.pax);
		setDatadogUserProperty("qrs_id", id);
	}, [response, actions]);

	return { isLoading, error, status: response?.data?.attributes?.status };
};

export default useCheckQrSession;
